import { useState } from 'react';

import shopify from '../../js/shopify';
import hasher from '../../js/alpine/stores/hasher';
import api from '../../js/api';

const useCreateCustomer = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [customerId, setCustomerId] = useState(null);

    const reset = () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        setCustomerId(null);
    };

    const createCustomer = async (formData) => {
        reset();

        try {
            // Step 1: Create customer using Storefront API (standard approach)
            console.log('Creating customer via Storefront API...');
            
            // Use the predefined query from the hasher store
            let storefrontQuery = hasher.store().getQuery('create_customer');
            if (!storefrontQuery) {
                console.error('create_customer query not found, falling back to inline query');
                storefrontQuery = `
                    mutation customerCreate($input: CustomerCreateInput!) {
                        customerCreate(input: $input) {
                            customer {
                                id
                                email
                                firstName
                                lastName
                            }
                            customerUserErrors {
                                code
                                field
                                message
                            }
                        }
                    }
                `;
            }

            // Format phone number for Shopify (ensure proper international format)
            let formattedPhone = null;
            if (formData.phone && formData.phone.trim()) {
                let phone = formData.phone.trim().replace(/[\s\-\(\)\.\+]/g, ''); // Remove all formatting including dots and plus
                
                // Only process if we have digits
                if (/^\d+$/.test(phone)) {
                    // Add country code based on country selection
                    if (formData.country === 'Australia') {
                        // Australian numbers: remove leading 0 if present, add +61
                        if (phone.startsWith('0') && phone.length >= 10) {
                            phone = '+61' + phone.substring(1);
                        } else if (phone.length >= 9) {
                            phone = '+61' + phone;
                        } else {
                            console.warn('Invalid Australian phone number length:', phone);
                            phone = null; // Skip invalid phone
                        }
                    } else if (formData.country === 'United States' || formData.country === 'Canada') {
                        // North American numbers: should be 10 digits
                        if (phone.length === 10) {
                            phone = '+1' + phone;
                        } else if (phone.length === 11 && phone.startsWith('1')) {
                            phone = '+' + phone;
                        } else {
                            console.warn('Invalid US/Canada phone number length:', phone);
                            phone = null; // Skip invalid phone
                        }
                    } else {
                        // Generic international: assume it's complete if 7+ digits
                        if (phone.length >= 7) {
                            phone = '+' + phone;
                        } else {
                            console.warn('Phone number too short:', phone);
                            phone = null; // Skip invalid phone
                        }
                    }
                    
                    // Final validation: ensure phone is reasonable length (8-15 digits after +)
                    if (phone && phone.length >= 8 && phone.length <= 16) {
                        formattedPhone = phone;
                    } else {
                        console.warn('Phone number failed final validation:', phone);
                        formattedPhone = null;
                    }
                } else {
                    console.warn('Phone contains non-numeric characters after cleanup:', formData.phone);
                }
            }

            // Prepare Admin API variables for customer creation (all fields including metafields)
            const metafieldArray = [
                formData.gender && {
                    namespace: "custom",
                    key: "gender",
                    type: "single_line_text_field",
                    value: formData.gender,
                },
                formData.state && {
                    namespace: "custom",
                    key: "state", 
                    type: "single_line_text_field",
                    value: formData.state,
                },
                formData.country && {
                    namespace: "custom",
                    key: "country",
                    type: "single_line_text_field", 
                    value: formData.country,
                },
            ].filter(Boolean);

            const storefrontVariables = {
                input: {
                    email: formData.email,
                    password: formData.password,
                    ...(formData.first_name && { firstName: formData.first_name }),
                    ...(formData.last_name && { lastName: formData.last_name }),
                    ...(formattedPhone && { phone: formattedPhone }),
                    ...(formData.optInEmail && { acceptsMarketing: true }),
                }
            };

            // console.log('Creating customer via Storefront API:', { 
            //     email: formData.email, 
            //     originalPhone: formData.phone || 'none',
            //     formattedPhone: formattedPhone || 'skipped',
            //     country: formData.country
            // });

            const token = api.getStorefrontToken();
            await hasher.store().dispatchHash();
            
            const storefrontResponse = await fetch('/api/2024-07/graphql.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': token,
                    'X-MindArc-Hash': hasher.store().getHash('create_customer'),
                },
                body: JSON.stringify({
                    query: storefrontQuery,
                    variables: storefrontVariables,
                }),
            });

            const storefrontData = await storefrontResponse.json();

            if (storefrontData.errors || storefrontData.data?.customerCreate?.customerUserErrors?.length > 0) {
                let errorMessage = '';
                console.error('Failed to create customer via Storefront API', storefrontData);
                
                if (storefrontData.errors) {
                    errorMessage = storefrontData.errors.map(err => err.message).join(', ');
                } else if (storefrontData.data?.customerCreate?.customerUserErrors?.length > 0) {
                    errorMessage = storefrontData.data.customerCreate.customerUserErrors
                        .map(err => `${err.field ? err.field + ': ' : ''}${err.message}`)
                        .join(', ');
                }
                
                throw new Error(errorMessage || 'Failed to create customer');
            }

            const customer = storefrontData.data?.customerCreate?.customer;
            if (!customer?.id) {
                throw new Error('Failed to create customer - no ID returned');
            }

            // console.log('Customer created via Storefront API:', {
            //     id: customer.id,
            //     email: customer.email
            // });

            // Step 2: Update customer with metafields using Admin API
            if (metafieldArray.length > 0 || formData.tags) {
                
                const adminQuery = hasher.store().getQuery('customer_metafields_update');
                const adminVariables = {
                    input: {
                        id: customer.id,
                        ...(formData.tags && { 
                            tags: Array.isArray(formData.tags) ? formData.tags : [formData.tags] 
                        }),
                        ...(metafieldArray.length > 0 && { metafields: metafieldArray }),
                    }
                };

                try {
                    const adminData = {
                        options: {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                "X-Shopify-Access-Token": "<SHOPIFY_API_AUTHENTICATED_KEY>",
                                "X-MindArc-Hash": hasher.store().getHash('customer_metafields_update'),
                            },
                            body: JSON.stringify({ query: adminQuery, variables: adminVariables }),
                            request_body: adminQuery,
                        },
                    };

                    const adminResponse = await shopify.authenticatedFetch(adminData);
                    
                    if (adminResponse.errors || adminResponse.data?.customerUpdate?.userErrors?.length > 0) {
                        console.warn('Customer created but metafield update failed:', adminResponse);
                    } else {
                        console.log('Metafields updated successfully');
                    }
                } catch (adminError) {
                    console.warn('Admin API call failed:', adminError.message);
                }
            }

            // Step 3: Auto-login via Storefront API with delay to ensure customer is fully created
            await new Promise(resolve => setTimeout(resolve, 2000));

            let loginQuery = hasher.store().getQuery('customer_access_token_create');
            if (!loginQuery) {
                console.error('customer_access_token_create query not found, falling back to inline query');
                loginQuery = `
                    mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
                        customerAccessTokenCreate(input: $input) {
                            customerAccessToken {
                                accessToken
                                expiresAt
                            }
                            customerUserErrors {
                                code
                                field
                                message
                            }
                        }
                    }
                `;
            }

            const loginVariables = {
                input: {
                    email: formData.email,
                    password: formData.password
                }
            };

            const loginResponse = await fetch('/api/2024-07/graphql.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': token,
                    'X-MindArc-Hash': hasher.store().getHash('customer_access_token_create'),
                },
                body: JSON.stringify({
                    query: loginQuery,
                    variables: loginVariables,
                }),
            });

            const loginData = await loginResponse.json();
            console.log('Full auto-login response:', loginData);

            if (loginData.data?.customerAccessTokenCreate?.customerAccessToken?.accessToken) {
                const accessToken = loginData.data.customerAccessTokenCreate.customerAccessToken.accessToken;
                localStorage.setItem('accessToken', accessToken);
                console.log('Auto-login successful, redirecting to /account');

            } else {
                const errors = loginData.data?.customerAccessTokenCreate?.customerUserErrors || [];
                let errorMessage = 'Registration successful! Please log in to continue.';
                
                if (errors.length > 0) {
                    const errorCodes = errors.map(err => err.code);
                    console.log('Auto-login failed:', errors);
                    
                    if (errorCodes.includes('UNIDENTIFIED_CUSTOMER')) {
                        errorMessage = 'Registration successful! Please check your email to verify your account, then log in.';
                    } else if (errorCodes.includes('INVALID_CREDENTIALS')) {
                        errorMessage = 'Registration successful! There was an issue with auto-login, please log in manually.';
                    } else {
                        errorMessage = `Registration successful! ${errors.map(e => e.message).join(', ')} Please log in manually.`;
                    }
                } else {
                    console.log('Auto-login failed with unknown error:', loginData);
                }
            }

            setCustomerId(customer.id);
            setSuccess(true);
            return customer.id;
        } catch (error) {
            const errorMessage = error.message;
            switch(true){
                case errorMessage.includes('already been taken'):
                    setError( 'Email or phone number already taken');
                break;
                default:
                    setError(error.message || "Error occurred while creating the customer");
                break;
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        createCustomer,
        loading,
        error,
        success,
        customerId,
        reset,
    };
};

export default useCreateCustomer;
